import React from "react";
import {
  FaMedal,
  FaBullseye,
  FaChartLine,
  FaGraduationCap,
} from "react-icons/fa";
import { motion } from "framer-motion";

export default function AboutTimeline() {
  const timelineData = [
    {
      step: "01",
      title: "Our Legacy",
      description:
        "We are committed to shaping confident, disciplined, and ethically driven civil servants who serve the nation with integrity and purpose.",
      icon: <FaMedal size={32} />,
      watermark: <FaMedal size={120} />,
      bg: "bg-blue-50",
      textAccent: "text-blue-700",
      watermarkColor: "text-blue-200/40",
    },
    {
      step: "02",
      title: "Our Vision",
      description: `To empower aspirants with the right blend of knowledge, strategy, and guidance — so every effort brings them closer to their dream.`,
      icon: <FaBullseye size={32} />,
      watermark: <FaBullseye size={120} />,
      bg: "bg-red-50",
      textAccent: "text-red-700",
      watermarkColor: "text-red-200/40",
    },
    {
      step: "03",
      title: "Our Edge",
      description:
        "We focus on consistent monitoring and improvement. Personalized attention and regular feedback are the real game-changers in UPSC preparation.",
      icon: <FaGraduationCap size={32} />,
      watermark: <FaGraduationCap size={120} />,
      bg: "bg-purple-50",
      textAccent: "text-purple-700",
      watermarkColor: "text-purple-200/40",
    },
    {
      step: "04",
      title: "Our Approach",
      description: [
        "Structured Curriculum",
        "Dedicated Faculty Support",
        "Consistent Follow-Up",
        "Exam-Focused Practice",
      ],
      icon: <FaChartLine size={32} />,
      watermark: <FaChartLine size={120} />,
      bg: "bg-green-50",
      textAccent: "text-green-700",
      watermarkColor: "text-green-200/40",
    },
  ];

  return (
    <section className="w-full flex justify-center items-center py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl w-full px-6 lg:px-8 relative z-10">
        {/* Title */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <div className="inline-block mb-4 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-[#1a237e] text-sm font-bold tracking-wider uppercase shadow-sm">
            About Us
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight">
            The Selva's <span className="text-blue-700">Advantage</span>
          </h2>
        </motion.div>

        {/* Soft Colored Blocks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {timelineData.map((item, index) => (
            <motion.div
              key={index}
              className={`relative flex flex-col p-8 rounded-[2rem] overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-slate-200/50 ${item.bg}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                ease: "easeOut",
                delay: index * 0.15,
              }}
              viewport={{ once: true }}
            >
              {/* Large Watermark Icon */}
              <div className={`absolute -bottom-6 -right-6 ${item.watermarkColor} transform -rotate-12 pointer-events-none transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-0`}>
                {item.watermark}
              </div>

              {/* Header: Step & Icon */}
              <div className="flex justify-between items-start mb-8 relative z-10">
                <span className={`text-5xl font-black opacity-20 ${item.textAccent}`}>
                  {item.step}
                </span>
                <div className={`${item.textAccent}`}>
                  {item.icon}
                </div>
              </div>

              {/* Content */}
              <div className="relative z-10 flex-grow">
                <h3 className="font-extrabold text-gray-900 text-2xl mb-4">
                  {item.title}
                </h3>

                {Array.isArray(item.description) ? (
                  <ul className="text-gray-700 text-base font-medium space-y-2">
                    {item.description.map((point, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${item.textAccent} opacity-60`}></div>
                        {point}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-700 text-base font-medium leading-relaxed">
                    {item.description}
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
